import {
  Box,
  Divider,
  Image,
  InlineStack,
  OptionCard,
  Paragraph,
  type OptionCardProps,
} from '@customerio/pluma-components/react';
import type { Ref } from 'react';
import clsx from 'clsx';
import apnsLogo from '@/assets/images/push-providers/apns.png';
import fcmLogo from '@/assets/images/push-providers/fcm.png';
import type { PushProvider } from '../types';
import * as styles from '../styles.css';

const PROVIDERS = {
  apns: { logo: apnsLogo, title: 'Apple Push Notification Service' },
  fcm: { logo: fcmLogo, title: 'Firebase Cloud Messaging' },
} satisfies Record<PushProvider, { logo: string; title: string }>;

export type ProviderOptionCardProps = {
  provider: PushProvider;
  ref?: Ref<HTMLLabelElement>;
} & Omit<OptionCardProps, 'children' | 'title'>;

/**
 * One choice in a push provider chooser. The caller supplies the group `name`,
 * the selection state and any layout it needs; the card only knows how a
 * provider presents itself.
 */
export function ProviderOptionCard({ provider, className, ...optionCardProps }: ProviderOptionCardProps) {
  const { logo, title } = PROVIDERS[provider];

  return (
    // The card's content is an image and a heading-weight line rather than a
    // form label, so the radio is named explicitly.
    <OptionCard
      value={provider}
      ariaLabel={title}
      className={clsx(styles.providerOptionCard, className)}
      {...optionCardProps}
    >
      <Box w="full">
        <InlineStack h="full" gap="200" alignBlock="center">
          <Image src={logo} alt={title} size="icon-v2-lg" />
          <Divider h="full" direction="vertical" />
          <Paragraph weight="bold">{title}</Paragraph>
        </InlineStack>
      </Box>
    </OptionCard>
  );
}
